"use client"

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Textarea,
  Select,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Badge,
  Image,
  AspectRatio,
  SimpleGrid,
  Divider,
  Spinner,
} from "@chakra-ui/react"
import {
  PlusCircleIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { selectCompanyDetails, fetchCompanyDetailsAsync } from "../../Company/CompanySingleSlice/CompanySingleSlice"
import noData from "../components/nodata1.png"
import { Media_View } from "../../common/URLs"
import {
  addCompanyInterviewsAsync,
  deleteCompanyInterviewAsync,
  updateCompanyInterviewAsync,
} from "../employerInterviewProcess/EmployerInterviewSlice"
import {
  addCompanyMediaAsync,
  deleteCompanyMediaAsync,
  selectCompanyMedia,
  updateCompanyMediaAsync,
} from "./EmployerMediaSlice"

function AddInterviewProcessModal({ isOpen, onClose, uuid, editingInterview = null }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    sort_order: 1,
    status: "active",
  })
  const dispatch = useDispatch()
  const toast = useToast()

  useEffect(() => {
    if (editingInterview) {
      setFormData(editingInterview)
    } else {
      setFormData({
        title: "",
        description: "",
        sort_order: 1,
        status: "active",
      })
    }
  }, [editingInterview])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingInterview) {
        await dispatch(updateCompanyInterviewAsync({ uuid: editingInterview.uuid, updatedData: formData })).unwrap()
        toast({
          title: "Interview process updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      } else {
        await dispatch(addCompanyInterviewsAsync(formData)).unwrap()
        toast({
          title: "Interview process added",
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      }
      onClose()
      dispatch(fetchCompanyDetailsAsync({ uuid }))
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="blue.500" color="white">
          {editingInterview ? "Edit" : "Add"} Interview Process
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter title"
                  size="lg"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  size="lg"
                  minH="120px"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Sort Order</FormLabel>
                <Input
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleChange}
                  placeholder="Enter sort order"
                  size="lg"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="status" mb="0" mr={4}>
                  Status
                </FormLabel>
                <Switch
                  id="status"
                  name="status"
                  isChecked={formData.status === "active"}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, status: e.target.checked ? "active" : "inactive" }))
                  }
                  size="lg"
                />
              </FormControl>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit} size="lg">
            {editingInterview ? "Update" : "Add"} Process
          </Button>
          <Button variant="ghost" onClick={onClose} size="lg">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function AddCompanyMediaModal({ isOpen, onClose, uuid, editingMedia = null }) {
  const [formData, setFormData] = useState({
    title: "",
    type: "",
    file: null,
    sort_order: "",
    status: "active",
  })

  const [previewUrl, setPreviewUrl] = useState("")
  const dispatch = useDispatch()
  const toast = useToast()

  useEffect(() => {
    if (editingMedia) {
      setFormData({
        ...editingMedia,
        file: null,
      })
      setPreviewUrl(editingMedia.file ? `${Media_View}/${editingMedia.file}` : "")
    } else {
      setFormData({
        title: "",
        type: "",
        file: null,
        sort_order: "",
        status: "active",
      })
      setPreviewUrl("")
    }
  }, [editingMedia])

  const handleChange = (e) => {
    const { name, value, type, files } = e.target

    if (type === "file") {
      const file = files[0]
      setFormData((prev) => ({ ...prev, file }))
      setPreviewUrl(URL.createObjectURL(file))
    } else if (name === "status") {
      setFormData((prev) => ({
        ...prev,
        status: value === "true" ? "active" : "inactive",
      }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.title || !formData.type || (!formData.file && !editingMedia) || !formData.sort_order) {
      toast({
        title: "Missing fields",
        description: "Please fill out all required fields.",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // console.log("form", formData)

    try {
      let result
      if (editingMedia) {
        result = await dispatch(updateCompanyMediaAsync({ uuid: editingMedia?.uuid, data: formData })).unwrap()
      } else {
        result = await dispatch(addCompanyMediaAsync({ data: formData })).unwrap()
      }

      toast({
        title: `Media ${editingMedia ? "updated" : "added"} successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })

      onClose()
      dispatch(fetchCompanyDetailsAsync({ uuid }))
    } catch (error) {
      console.error("Error submitting form:", error)
      toast({
        title: `Failed to ${editingMedia ? "update" : "add"} media`,
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="teal.500" color="white">
          {editingMedia ? "Edit" : "Add"} Company Media
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  name="title"
                  placeholder="Enter title"
                  value={formData.title}
                  onChange={handleChange}
                  size="lg"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Media Type</FormLabel>
                <Select
                  name="type"
                  placeholder="Select media type"
                  value={formData.type}
                  onChange={handleChange}
                  size="lg"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </Select>
              </FormControl>

              <FormControl isRequired={!editingMedia}>
                <FormLabel>File</FormLabel>
                <Input name="file" type="file" onChange={handleChange} size="lg" pt={1} />
              </FormControl>

              {previewUrl && (
                <Box w="full">
                  <Text fontWeight="bold" mb={2}>
                    Preview:
                  </Text>
                  {formData.type === "image" ? (
                    <Image
                      src={previewUrl || "/placeholder.svg"}
                      alt="Preview"
                      maxH="200px"
                      objectFit="contain"
                      mx="auto"
                    />
                  ) : (
                    <video src={previewUrl} style={{ maxHeight: "200px", margin: "0 auto" }} controls />
                  )}
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Sort Order</FormLabel>
                <Input
                  name="sort_order"
                  type="number"
                  placeholder="Enter sort order"
                  value={formData.sort_order}
                  onChange={handleChange}
                  size="lg"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="status" mb="0" mr={4}>
                  Status
                </FormLabel>
                <Switch
                  id="status"
                  name="status"
                  isChecked={formData.status === "active"}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "status", value: e.target.checked.toString() },
                    })
                  }
                  size="lg"
                />
              </FormControl>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="teal" mr={3} onClick={handleSubmit} size="lg">
            {editingMedia ? "Update" : "Add"} Media
          </Button>
          <Button variant="ghost" onClick={onClose} size="lg">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, itemType }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="red.500" color="white">
          Confirm Deletion
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <Text fontSize="lg">Are you sure you want to delete this {itemType}?</Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            This action cannot be undone.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={onConfirm} size="lg">
            Delete
          </Button>
          <Button variant="ghost" onClick={onClose} size="lg">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default function EmployerMedia({ uuid }) {
  const { isOpen: isInterviewOpen, onOpen: onInterviewOpen, onClose: onInterviewClose } = useDisclosure()
  const { isOpen: isMediaOpen, onOpen: onMediaOpen, onClose: onMediaClose } = useDisclosure()
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()
  const toast = useToast()
  const dispatch = useDispatch()
  const [editingInterview, setEditingInterview] = useState(null)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [editingMedia, setEditingMedia] = useState(null)
  const companyDetails = useSelector(selectCompanyDetails)
  const companyMedia = useSelector(selectCompanyMedia)
  const loading = useSelector((state) => state.companyDetails.loading)
  const error = useSelector((state) => state.companyDetails.error)

  useEffect(() => {
    const fetchData = async () => {
      if (uuid) {
        dispatch(fetchCompanyDetailsAsync({ uuid }))
      }
    }
    fetchData()
  }, [uuid, dispatch])

  // In your EmployerMedia component, modify the error effect:
  // useEffect(() => {
  //   if (error && !loading && !companyDetails) {
  //     toast({
  //       title: "Error",
  //       description: "Failed to fetch company details",
  //       status: "error",
  //       duration: 3000,
  //       isClosable: true,
  //       position: "top-right"
  //     });
  //   }
  // }, [error, loading, companyDetails, toast]);

  const getStatusBadge = (status) => {
    return (
      <Badge
        colorScheme={status === "active" ? "green" : "red"}
        display="flex"
        alignItems="center"
        px={2}
        py={1}
        borderRadius="full"
      >
        {status === "active" ? <CheckCircleIcon className="h-4 w-4 mr-1" /> : <XCircleIcon className="h-4 w-4 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const handleDeleteItem = (item, type) => {
    setItemToDelete({ item, type })
    onDeleteModalOpen()
  }

  const confirmDeleteItem = async () => {
    try {
      if (itemToDelete.type === "interview") {
        await dispatch(deleteCompanyInterviewAsync(itemToDelete.item.uuid)).unwrap()
      } else if (itemToDelete.type === "media") {
        await dispatch(deleteCompanyMediaAsync(itemToDelete.item.uuid)).unwrap()
      }
      toast({
        title: `${itemToDelete.type.charAt(0).toUpperCase() + itemToDelete.type.slice(1)} deleted`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })
      dispatch(fetchCompanyDetailsAsync({ uuid }))
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
    onDeleteModalClose()
  }

  const handleEditInterview = (interview) => {
    setEditingInterview(interview)
    onInterviewOpen()
  }

  const handleEditMedia = (media) => {
    setEditingMedia(media)
    onMediaOpen()
  }

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <VStack spacing={6} width="full">
          <Spinner size="xl" color="blue.500" />
          <Text mt={4} fontSize="lg" fontWeight="medium">
            Loading company details...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (error && !companyDetails) {
    return (
      <Box textAlign="center" py={10}>
        <VStack spacing={6} width="full">
          <XCircleIcon className="h-12 w-12 text-red-500" />
          <Text mt={4} fontSize="lg" fontWeight="medium" color="red.500">
            Failed to load company details
          </Text>
          <Button
            onClick={() => dispatch(fetchCompanyDetailsAsync({ uuid }))}
            colorScheme="blue"
          >
            Retry
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh">
      <div>
        <VStack spacing={6} align="stretch">
          <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" borderRadius="lg">
            <Box>
              <div as={"h2"} mb={2} className="font-semibold text-2xl text-gray-700">
                Company Management
              </div>
              <Text color="gray.600" fontSize="md">
                Manage interview processes and company media
              </Text>
            </Box>
            <HStack mt={{ base: 4, md: 0 }} className="flex flex-wrap">
              <Button
                leftIcon={<PlusCircleIcon className="h-5 w-5" />}
                onClick={onInterviewOpen}
                colorScheme="blue"
                size="md"
                fontWeight="semi-bold"
                // _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Add Interview Process
              </Button>
              <Button
                leftIcon={<PhotoIcon className="h-5 w-5" />}
                onClick={onMediaOpen}
                colorScheme="teal"
                size="md"
                fontWeight="semi-bold"
                // _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Add Company Media
              </Button>
            </HStack>
          </Flex>

          <Tabs isFitted variant="enclosed" colorScheme="blue">
            <TabList mb="1em">
              <Tab fontWeight="medium" fontSize="sm" _selected={{ color: "blue.600", borderColor: "blue.600" }}>
                Interview Processes
              </Tab>
              <Tab fontWeight="medium" fontSize="sm" _selected={{ color: "blue.600", borderColor: "blue.600" }}>
                Company Media
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <VStack spacing={3} align="stretch">
                  {companyDetails?.company_interview_process?.length > 0 ? (
                    companyDetails?.company_interview_process?.map((process) => (
                      <Box
                        key={process.uuid}
                        p={6}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg="white"
                        // boxShadow="md"
                        transition="all 0.3s"
                        className="flex-flex-wrap"
                      >
                        <Box position="relative">
                          <VStack align="start" spacing={2}>
                            <Flex className="flex justify-between w-full">
                              <Heading size="md" color="gray.700">
                                {process.title}
                              </Heading>
                              <Box zIndex="1">{getStatusBadge(process.status)}</Box>
                            </Flex>
                            <Text fontSize="md" color="gray.600">
                              {process.description}
                            </Text>
                          </VStack>
                        </Box>
                        <Divider my={4} />
                        <Flex justifyContent="space-between" alignItems="center" wrap={"wrap"}>
                          <HStack alignItems={"start"}>
                            <Box>
                              {process.sort_order <= 1 ? (
                                <ArrowUpIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <ArrowDownIcon className="h-5 w-5 text-blue-500" />
                              )}
                            </Box>
                            <Text fontSize="md" fontWeight="medium" color="gray.600">
                              Order: {process.sort_order}
                            </Text>
                          </HStack>
                          <HStack>
                            <Button
                              leftIcon={<PencilIcon className="h-4 w-4" />}
                              onClick={() => handleEditInterview(process)}
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                            >
                              Edit
                            </Button>
                            <Button
                              leftIcon={<TrashIcon className="h-4 w-4" />}
                              onClick={() => handleDeleteItem(process, "interview")}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                            >
                              Delete
                            </Button>
                          </HStack>
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center w-full">
                      <img src={noData} className="object-contain h-[400px] w-[400px]" />
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">No Interview Processes Found</h3>
                      <p className="text-gray-600 text-center max-w-md">
                        We couldn't find any Interview Processes at the moment. Please add one using the button above.
                      </p>
                    </div>
                  )}
                </VStack>
              </TabPanel>
              <TabPanel>
                {companyDetails?.company_media?.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                    {companyDetails?.company_media?.map((media) => (
                      <Box
                        key={media.uuid}
                        borderWidth="1px"
                        borderRadius="lg"
                        overflow="hidden"
                        bg="white"
                        transition="all 0.3s"
                        _hover={{ boxShadow: "lg" }}
                      >
                        <AspectRatio ratio={16 / 9}>
                          {media.type === "image" ? (
                            <Image
                              src={media.file ? `${Media_View}/${media.file}` : "/placeholder.svg"}
                              alt={media.title}
                              objectFit="cover"
                            />
                          ) : (
                            <video controls width="100%" height="100%">
                              <source src={media.file ? `${Media_View}/${media.file}` : ""} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )}
                        </AspectRatio>
                        <Box p={4}>
                          <Flex justifyContent="space-between" alignItems="center" mb={2} wrap={"wrap"}>
                            <Heading size="md" color="gray.700">
                              {media.title}
                            </Heading>
                            {getStatusBadge(media.status)}
                          </Flex>
                          <Divider my={3} />
                          <Flex justifyContent="space-between" alignItems="center" wrap={"wrap"}>
                            <HStack alignItems={"start"}>
                              <Box>
                                {media.sort_order <= 1 ? (
                                  <ArrowUpIcon className="h-5 w-5 text-green-500" />
                                ) : (
                                  <ArrowDownIcon className="h-5 w-5 text-blue-500" />
                                )}
                              </Box>
                              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                                Order: {media.sort_order}
                              </Text>
                            </HStack>
                            <HStack>
                              <Button
                                leftIcon={<PencilIcon className="h-4 w-4" />}
                                onClick={() => handleEditMedia(media)}
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                              >
                                Edit
                              </Button>
                              <Button
                                leftIcon={<TrashIcon className="h-4 w-4" />}
                                onClick={() => handleDeleteItem(media, "media")}
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                              >
                                Delete
                              </Button>
                            </HStack>
                          </Flex>
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : (
                  <div className="flex flex-col items-center justify-center w-full">
                    <img src={noData} className="object-contain h-[400px] w-[400px]" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No Company Media Found</h3>
                    <p className="text-gray-600 text-center max-w-md">
                      We couldn't find any Company Media at the moment. Please add some using the button above.
                    </p>
                  </div>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </div>
      <AddInterviewProcessModal
        isOpen={isInterviewOpen}
        onClose={() => {
          onInterviewClose()
          setEditingInterview(null)
        }}
        uuid={uuid}
        editingInterview={editingInterview}
      />
      <AddCompanyMediaModal
        isOpen={isMediaOpen}
        onClose={() => {
          onMediaClose()
          setEditingMedia(null)
        }}
        uuid={uuid}
        editingMedia={editingMedia}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={confirmDeleteItem}
        itemType={itemToDelete?.type}
      />
    </Box>
  )
}
